import { ChangeEvent, memo, MouseEvent, ReactElement, ReactNode } from 'react';
import useStyles from './styles';
/* Components */
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogTitleProps,
  DialogContentProps,
  DialogProps,
  IconButton,
  ClassNameMap,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

type DesktopDialogProps = Partial<DialogProps> & {
  title?: string | ReactElement;
  titleElement?: ReactElement;
  onClose?: () => void;
  onCloseBtnPressed?: () => void;
  DialogContentProps?: DialogContentProps;
  DialogTitleProps?: DialogTitleProps;
  saving?: boolean;
  actions?: ReactNode;
  hideCloseIcon?: boolean;
  middleContent?: ReactElement | null;
};

export default memo<DesktopDialogProps>(
  ({
    title,
    titleElement,
    onClose,
    onCloseBtnPressed,
    children,
    DialogContentProps,
    DialogTitleProps,
    actions,
    hideCloseIcon,
    classes = {},
    middleContent,
    ...props
  }) => {
    const customClasses: ClassNameMap = useStyles();

    const handleClose = (e: ChangeEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      onClose !== undefined && onClose();
    };

    const handleCloseBtnPressed = (e: MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      onCloseBtnPressed !== undefined && onCloseBtnPressed();
      onClose !== undefined && onClose();
    };

    return (
      <Dialog
        open
        onClose={handleClose}
        classes={{
          ...classes,
          paperWidthLg: customClasses.paperWidthLg,
          paperWidthMd: customClasses.paperWidthMd,
          paperWidthSm: customClasses.paperWidthSm,
        }}
        {...props}
        disableEnforceFocus={true}
      >
        {title && (
          <DialogTitle
            className={customClasses.dialogTitle}
            {...DialogTitleProps}
          >
            {title}
            {!hideCloseIcon && (
              <IconButton
                size="small"
                aria-label="close"
                onClick={handleCloseBtnPressed}
              >
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
        )}
        {titleElement && (
          <DialogTitle {...DialogTitleProps}>
            {titleElement}
            {!hideCloseIcon && (
              <IconButton
                size="small"
                aria-label="close"
                onClick={handleCloseBtnPressed}
              >
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
        )}
        <DialogContent
          {...DialogContentProps}
          classes={
            DialogContentProps?.classes
              ? {
                  ...DialogContentProps.classes,
                  root: `${DialogContentProps.classes?.root ?? ''} ${
                    customClasses.dialogContentRoot
                  }`,
                }
              : {
                  root: customClasses.dialogContentRoot,
                }
          }
        >
          {children}
        </DialogContent>
        {middleContent && middleContent}
        {actions && <DialogActions>{actions}</DialogActions>}
      </Dialog>
    );
  }
);
